import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  ReactFlow,
  Node,
  Edge,
  Connection,
  useNodesState,
  useEdgesState,
  addEdge,
  Background,
  BackgroundVariant,
  Panel,
  useReactFlow,
  ReactFlowProvider,
  reconnectEdge,
  MarkerType,
  type EdgeMouseHandler,
  type OnReconnect,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  Box,
  Paper,
  IconButton,
  Button,
  Tooltip,
  Drawer,
  Typography,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  Add as AddIcon,
  RemoveCircleOutline as RemoveFromDiagramIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  OpenInNew as OpenInNewIcon,
  GridOn as GridOnIcon,
  ExitToApp as ExitToAppIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toPng } from 'html-to-image';
import { RelationForm } from '../Relations/RelationForm';
import { relationsService } from '../../services/relations.service';
import { componentsService } from '../../services/components.service';
import { riskApi } from '../../services/risk.service';
import { CustomNode, ZoneNode } from './CustomNode';
import { ComponentSelector } from './ComponentSelector';
import { ComponentFormWizard } from '../ComponentForm/ComponentFormWizard';
import { useSecurityAssistantOptional } from '../../contexts/SecurityAssistantContext';
import { getOptimalHandles } from './layoutUtils';
import { type CreateComponentDto, type Component } from 'shared';
import type {
  Diagram,
  DiagramData,
} from '../../../../shared/src/types/space.types';
import type { ComponentType } from '../../../../shared/src/types/relation-mappings.types';
import type {
  RelationType,
  CriticalityLevel,
} from '../../../../shared/src/types/relation.types';
import type { CreateRelationDto } from '../../services/relations.service';

// Custom node types for ReactFlow
const nodeTypes = {
  custom: CustomNode,
  zone: ZoneNode,
};

// Helper: Topological sort to ensure parent nodes come before children

function topologicalSortNodes<T extends { id: string; parentId?: string }>(
  nodes: T[]
): T[] {
  const nodeMap = new Map(nodes.map(n => [n.id, n]));
  const visited = new Set<string>();
  const result: T[] = [];

  function visit(nodeId: string) {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);

    const node = nodeMap.get(nodeId);
    if (!node) return;

    // Visit parent first if exists
    if (node.parentId && nodeMap.has(node.parentId)) {
      visit(node.parentId);
    }

    result.push(node);
  }

  // Visit all nodes
  for (const node of nodes) {
    visit(node.id);
  }

  return result;
}

interface DiagramBuilderProps {
  diagramId?: string;
  spaceId: string;
  onSave?: (diagramData: DiagramData, thumbnail: string) => Promise<void>;
  initialData?: Diagram;
  isReadOnly?: boolean;
}

const DiagramBuilderComponent: React.FC<DiagramBuilderProps> = ({
  onSave,
  initialData,
  spaceId,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const reactFlowInstance = useReactFlow();

  const reactFlowWrapperRef = useRef<HTMLDivElement>(null);

  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isSidebarOpen, _setIsSidebarOpen] = useState(true);
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [relationFormOpen, setRelationFormOpen] = useState(false);
  const [componentFormOpen, setComponentFormOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    nodeId: string;
  } | null>(null);
  const [edgeContextMenu, setEdgeContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    edgeId: string;
    relationId: string;
  } | null>(null);
  const [relationFormMode, setRelationFormMode] = useState<'create' | 'edit'>(
    'create'
  );
  const [editingRelation, setEditingRelation] = useState<{
    id: string;
    type: string;
    fromEntityId: string;
    toEntityId: string;
    description?: string;
    criticality?: 'critical' | 'high' | 'medium' | 'low';
  } | null>(null);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState<{
    type: 'node' | 'edge';
    id: string;
    name: string;
    isGlobal?: boolean;
    relationCount?: number;
    loading?: boolean;
  } | null>(null);
  const [pendingConnection, setPendingConnection] = useState<Connection | null>(
    null
  );
  const [fromComponent, setFromComponent] = useState<{
    id: string;
    name: string;
    type: ComponentType;
  } | null>(null);
  const [toComponent, setToComponent] = useState<{
    id: string;
    name: string;
    type: ComponentType;
  } | null>(null);
  const [_selectedComponent, setSelectedComponent] = useState<Component | null>(
    null
  );

  // Track if user is currently dragging a connection
  const [isConnecting, setIsConnecting] = useState(false);

  // Security Assistant context for AI-powered suggestions (optional for embed/read-only mode)
  const securityAssistant = useSecurityAssistantOptional();
  const setComponentContext = securityAssistant?.setComponentContext;
  const clearContext = securityAssistant?.clearContext;

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({ open: false, message: '', severity: 'success' });

  // Load initial diagram data
  useEffect(() => {
    const loadDiagramData = async () => {
      if (!initialData?.diagramData) return;

      const {
        nodes: loadedNodes,
        edges: loadedEdges,
        canvasState,
      } = initialData.diagramData;

      // Build a set of all node IDs for validation
      const allNodeIds = new Set(loadedNodes.map(n => n.id));

      // Clean up invalid parent references and sort nodes
      const cleanedNodes = loadedNodes.map(node => {
        // If parentId references a non-existent node, remove the reference
        if (node.parentId && !allNodeIds.has(node.parentId)) {
          console.warn(
            `Removing invalid parentId ${node.parentId} from node ${node.id}`
          );
          return { ...node, parentId: undefined };
        }
        return node;
      });

      // Topological sort: parents must come before children
      const sortedNodes = topologicalSortNodes(cleanedNodes);

      // Fetch component data for all nodes
      const componentIds = sortedNodes
        .map(n => n.componentId)
        .filter(id => id && !id.startsWith('temp-'));

      let componentsMap = new Map<string, Component>();
      let risksMap = new Map<string, { highestScore: number; count: number }>();

      if (componentIds.length > 0) {
        try {
          // Fetch components
          console.info(
            '[DiagramBuilder] Fetching components for IDs:',
            componentIds
          );
          const components = await componentsService.getByIds(componentIds);
          console.info(
            '[DiagramBuilder] Fetched components:',
            components.length
          );
          componentsMap = new Map(components.map(c => [c.id, c]));

          // Fetch risks for all components in space
          console.info('[DiagramBuilder] Fetching risks for space:', spaceId);
          const allRisks = await riskApi.getAll({ spaceId });
          console.info('[DiagramBuilder] Fetched risks:', allRisks.length);

          // Group risks by componentId and calculate highest residual risk
          for (const risk of allRisks) {
            if (risk.componentId) {
              const existing = risksMap.get(risk.componentId);
              const riskScore = risk.residualRisk ?? risk.inherentRisk ?? 0;
              if (existing) {
                risksMap.set(risk.componentId, {
                  highestScore: Math.max(existing.highestScore, riskScore),
                  count: existing.count + 1,
                });
              } else {
                risksMap.set(risk.componentId, {
                  highestScore: riskScore,
                  count: 1,
                });
              }
            }
          }
          console.info(
            '[DiagramBuilder] Risk map built for',
            risksMap.size,
            'components'
          );
        } catch (error) {
          console.error('Failed to fetch components or risks:', error);
        }
      }

      // Convert diagram nodes to ReactFlow nodes with actual component data
      const flowNodes: Node[] = sortedNodes.map(nodeData => {
        const component = componentsMap.get(nodeData.componentId);
        const riskInfo = risksMap.get(nodeData.componentId);
        const componentType = component?.type || nodeData.type || 'application';
        // Use 'zone' node type for zone/security_zone components (renders as container)
        const isZoneType =
          componentType === 'zone' || componentType === 'security_zone';
        const reactFlowNodeType = isZoneType ? 'zone' : 'custom';
        const flowNode: Node = {
          id: nodeData.id,
          type: reactFlowNodeType,
          position: { x: nodeData.x, y: nodeData.y },
          data: {
            componentId: nodeData.componentId,
            label: component?.name || nodeData.id,
            type: componentType,
            // CIA scores
            confidentialityScore: component?.confidentialityScore,
            integrityScore: component?.integrityScore,
            availabilityScore: component?.availabilityScore,
            // Risk info
            highestRiskScore: riskInfo?.highestScore,
            riskCount: riskInfo?.count,
          },
          parentId: nodeData.parentId,
          extent: nodeData.parentId ? ('parent' as const) : undefined,
          // Set dimensions for both style and direct properties
          width: isZoneType ? nodeData.width || 500 : nodeData.width || 150,
          height: isZoneType ? nodeData.height || 400 : nodeData.height || 80,
          style:
            nodeData.style ||
            (isZoneType
              ? {
                  width: nodeData.width || 500,
                  height: nodeData.height || 400,
                }
              : {
                  width: nodeData.width,
                  height: nodeData.height,
                  backgroundColor: nodeData.color,
                }),
        };
        return flowNode;
      });

      // Build a set of valid node IDs for quick lookup (excluding zone nodes which have no handles)
      const validNodeIds = new Set(
        flowNodes.filter(n => n.type !== 'zone').map(n => n.id)
      );

      // Convert diagram edges to ReactFlow edges, filtering out invalid ones
      const flowEdges: Edge[] = loadedEdges
        .filter(edgeData => {
          // Filter out edges with missing or invalid source/target
          if (!edgeData.sourceNodeId || !edgeData.targetNodeId) return false;
          // Filter out edges connecting to/from zone nodes (they have no handles)
          if (!validNodeIds.has(edgeData.sourceNodeId)) return false;
          if (!validNodeIds.has(edgeData.targetNodeId)) return false;
          return true;
        })
        .map(edgeData => ({
          id: edgeData.id,
          source: edgeData.sourceNodeId,
          target: edgeData.targetNodeId,
          sourceHandle: edgeData.sourceHandle,
          targetHandle: edgeData.targetHandle,
          // Don't use saved label - it will be set when relations are loaded
          // This ensures translations are applied correctly
          label: '',
          type: 'smoothstep',
          style: {
            stroke: edgeData.color,
            strokeDasharray: edgeData.style === 'dashed' ? '5,5' : undefined,
          },
          data: { relationId: edgeData.relationId },
        }));

      // Update edge handles based on node positions if not already set
      const edgesWithOptimalHandles = flowEdges.map(edge => {
        // If handles are already set, keep them
        if (edge.sourceHandle && edge.targetHandle) return edge;

        // Find source and target nodes
        const sourceNode = flowNodes.find(n => n.id === edge.source);
        const targetNode = flowNodes.find(n => n.id === edge.target);

        if (!sourceNode || !targetNode) return edge;

        // Calculate optimal handles
        const { sourceHandle, targetHandle } = getOptimalHandles(
          sourceNode,
          targetNode
        );

        return {
          ...edge,
          sourceHandle: edge.sourceHandle || sourceHandle,
          targetHandle: edge.targetHandle || targetHandle,
        };
      });

      setNodes(flowNodes);
      setEdges(edgesWithOptimalHandles);

      // Restore canvas state
      if (canvasState) {
        window.setTimeout(() => {
          reactFlowInstance.setViewport({
            x: canvasState.offsetX,
            y: canvasState.offsetY,
            zoom: canvasState.zoom,
          });
        }, 0);
      }
    };

    loadDiagramData();
  }, [initialData, spaceId, setNodes, setEdges, reactFlowInstance]);

  // Load relations for all nodes on the diagram
  useEffect(() => {
    const loadRelationsForNodes = async () => {
      if (nodes.length === 0) return;

      // Get all unique component IDs from nodes
      const componentIds = nodes
        .map(n => String(n.data?.componentId))
        .filter(id => id && !id.startsWith('temp-'));

      if (componentIds.length === 0) return;

      try {
        // Fetch relations for all components
        const relationPromises = componentIds.map(id =>
          relationsService.getByEntityId(id).catch(() => [])
        );
        const relationsArrays = await Promise.all(relationPromises);

        // Flatten and dedupe relations
        const allRelations = relationsArrays.flat();
        const uniqueRelations = allRelations.filter(
          (rel, index, self) => self.findIndex(r => r.id === rel.id) === index
        );

        // Create edges for relations between nodes on this diagram (excluding zones)
        const nodeIdMap = new Map<string, string>();
        const zoneNodeIds = new Set<string>();
        nodes.forEach(n => {
          if (n.type === 'zone') {
            zoneNodeIds.add(n.id);
          } else if (n.data?.componentId) {
            nodeIdMap.set(String(n.data.componentId), n.id);
          }
        });

        const newEdges: Edge[] = [];
        const edgeUpdates: Map<
          string,
          { label: string; relationType: string }
        > = new Map();

        uniqueRelations.forEach(rel => {
          const sourceNodeId = nodeIdMap.get(rel.fromEntityId);
          const targetNodeId = nodeIdMap.get(rel.toEntityId);

          // Only create edge if both endpoints exist, are valid, and are not zone nodes
          if (
            sourceNodeId &&
            targetNodeId &&
            sourceNodeId !== 'null' &&
            targetNodeId !== 'null' &&
            !zoneNodeIds.has(sourceNodeId) &&
            !zoneNodeIds.has(targetNodeId)
          ) {
            // Check if edge already exists
            const existingEdge = edges.find(
              e =>
                e.data?.relationId === rel.id ||
                (e.source === sourceNodeId && e.target === targetNodeId)
            );

            const translatedLabel = t(`relation.types.${rel.type}`, {
              defaultValue: rel.type,
            });

            if (existingEdge) {
              // Update existing edge with translated label
              edgeUpdates.set(existingEdge.id, {
                label: translatedLabel,
                relationType: rel.type,
              });
            } else {
              newEdges.push({
                id: `edge-${rel.id}`,
                source: sourceNodeId,
                target: targetNodeId,
                label: translatedLabel,
                data: { relationId: rel.id, relationType: rel.type },
              });
            }
          }
        });

        // Apply updates to existing edges and add new edges
        if (newEdges.length > 0 || edgeUpdates.size > 0) {
          setEdges(prevEdges => {
            const updatedEdges = prevEdges.map(edge => {
              const update = edgeUpdates.get(edge.id);
              if (update) {
                return {
                  ...edge,
                  label: update.label,
                  data: { ...edge.data, relationType: update.relationType },
                };
              }
              return edge;
            });
            return [...updatedEdges, ...newEdges];
          });
        }
      } catch (error) {
        console.error('Failed to load relations:', error);
      }
    };

    // Only run once when nodes are loaded and stable
    const timeoutId = globalThis.setTimeout(loadRelationsForNodes, 500);
    return () => globalThis.clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes.length]); // Only depend on nodes.length to avoid infinite loops

  // Handle connection between nodes
  const onConnect = useCallback(
    async (connection: Connection) => {
      // Prevent self-connections
      if (connection.source === connection.target) {
        console.warn('Cannot connect a node to itself');
        return;
      }

      // Find the components for source and target nodes
      const sourceNode = nodes.find(n => n.id === connection.source);
      const targetNode = nodes.find(n => n.id === connection.target);

      if (!sourceNode || !targetNode) {
        console.warn('Could not find source or target node', {
          source: connection.source,
          target: connection.target,
          availableNodes: nodes.map(n => n.id),
        });
        return;
      }

      // Get component data from node data
      const fromComp = {
        id: String(sourceNode.data.componentId),
        name: String(sourceNode.data.label || sourceNode.id),
        type: (sourceNode.data.type || 'application') as ComponentType,
      };

      const toComp = {
        id: String(targetNode.data.componentId),
        name: String(targetNode.data.label || targetNode.id),
        type: (targetNode.data.type || 'application') as ComponentType,
      };

      setPendingConnection(connection);
      setFromComponent(fromComp);
      setToComponent(toComp);
      setRelationFormOpen(true);
    },
    [nodes]
  );

  // Helper function to sort nodes so parents come before children (ReactFlow requirement)
  const sortNodesParentFirst = useCallback((nodesToSort: Node[]): Node[] => {
    // Use topological sort to ensure correct parent-child ordering
    return topologicalSortNodes(nodesToSort);
  }, []);

  // Handle node drag stop - detect if node is dropped inside a zone
  const onNodeDragStop = useCallback(
    (_event: React.MouseEvent, draggedNode: Node) => {
      // Skip if the dragged node is itself a zone
      if (draggedNode.type === 'zone') return;

      // Find all zone nodes
      const zoneNodes = nodes.filter(n => n.type === 'zone');
      if (zoneNodes.length === 0) return;

      // Get the dragged node's position - need to convert to absolute if it has a parent
      const currentParentId = draggedNode.parentId;
      let absoluteX = draggedNode.position.x;
      let absoluteY = draggedNode.position.y;

      // If node has a parent, convert relative position to absolute
      if (currentParentId) {
        const currentParent = nodes.find(n => n.id === currentParentId);
        if (currentParent) {
          absoluteX += currentParent.position.x;
          absoluteY += currentParent.position.y;
        }
      }

      const draggedWidth = (draggedNode.style?.width as number) || 150;
      const draggedHeight = (draggedNode.style?.height as number) || 80;
      const draggedCenterX = absoluteX + draggedWidth / 2;
      const draggedCenterY = absoluteY + draggedHeight / 2;

      // Check if the center of the dragged node is inside any zone
      // If multiple zones overlap, prefer the smallest one (most specific)
      let newParentId: string | undefined = undefined;
      let smallestZoneArea = Infinity;

      for (const zone of zoneNodes) {
        const zoneWidth = (zone.style?.width as number) || 500;
        const zoneHeight = (zone.style?.height as number) || 400;
        const zoneX = zone.position.x;
        const zoneY = zone.position.y;

        // Check if dragged node's center is inside this zone
        if (
          draggedCenterX > zoneX &&
          draggedCenterX < zoneX + zoneWidth &&
          draggedCenterY > zoneY &&
          draggedCenterY < zoneY + zoneHeight
        ) {
          const zoneArea = zoneWidth * zoneHeight;
          // Prefer smaller zones (more specific containment)
          if (zoneArea < smallestZoneArea) {
            smallestZoneArea = zoneArea;
            newParentId = zone.id;
          }
        }
      }

      // If parent changed, update the node
      if (newParentId !== currentParentId) {
        setNodes(nds => {
          const updatedNodes = nds.map(n => {
            if (n.id !== draggedNode.id) return n;

            if (newParentId) {
              // Node is now inside a zone - set parent and convert to relative position
              const parentZone = zoneNodes.find(z => z.id === newParentId);
              if (parentZone) {
                // Use absolute position to calculate relative position to new parent
                const relativeX = absoluteX - parentZone.position.x;
                const relativeY = absoluteY - parentZone.position.y;
                return {
                  ...n,
                  position: { x: relativeX, y: relativeY },
                  parentId: newParentId,
                  extent: 'parent' as const,
                };
              }
            } else {
              // Node is outside all zones - remove parent, position is already absolute
              return {
                ...n,
                position: { x: absoluteX, y: absoluteY },
                parentId: undefined,
                extent: undefined,
              };
            }
            return n;
          });
          // Re-sort to ensure parents come before children
          return sortNodesParentFirst(updatedNodes);
        });
      }

      // Note: We do NOT auto-update edge handles on drag
      // This preserves user's manual handle selections
      // Use the Auto Layout button to recalculate all handles
    },
    [nodes, setNodes, sortNodesParentFirst]
  );

  // Handle save
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Get current viewport state
      const viewport = reactFlowInstance.getViewport();

      // Convert ReactFlow nodes back to diagram nodes
      const diagramNodes = nodes.map((node: Node) => {
        // Get current dimensions - for resizable zones, use measured dimensions if available
        let nodeWidth: number | undefined;
        let nodeHeight: number | undefined;

        if (node.type === 'zone') {
          // For zone nodes, prioritize measured dimensions (from resizing) over initial style
          nodeWidth =
            node.measured?.width || (node.style?.width as number) || 500;
          nodeHeight =
            node.measured?.height || (node.style?.height as number) || 400;
        } else {
          // For regular nodes, use measured or default dimensions
          nodeWidth =
            node.measured?.width || (node.style?.width as number) || 150;
          nodeHeight =
            node.measured?.height || (node.style?.height as number) || 80;
        }

        return {
          id: node.id,
          componentId: String(node.data.componentId || ''),
          x: node.position.x,
          y: node.position.y,
          width: nodeWidth,
          height: nodeHeight,
          color: node.style?.backgroundColor as string | undefined,
          parentId: node.parentId,
          type: node.type,
          style: node.style as Record<string, unknown> | undefined,
        };
      });

      // Convert ReactFlow edges back to diagram edges
      const diagramEdges = edges.map((edge: Edge) => ({
        id: edge.id,
        relationId: String(edge.data?.relationId || ''),
        sourceNodeId: edge.source,
        targetNodeId: edge.target,
        sourceHandle: edge.sourceHandle ?? undefined,
        targetHandle: edge.targetHandle ?? undefined,
        label: edge.label as string | undefined,
        color: edge.style?.stroke as string | undefined,
        style: edge.style?.strokeDasharray
          ? ('dashed' as const)
          : ('solid' as const),
      }));

      const diagramData: DiagramData = {
        nodes: diagramNodes,
        edges: diagramEdges,
        canvasState: {
          zoom: viewport.zoom,
          offsetX: viewport.x,
          offsetY: viewport.y,
        },
      };

      // Generate thumbnail using html-to-image (v1.11.11 - last working version)
      let thumbnail = '';

      if (reactFlowWrapperRef.current && nodes.length > 0) {
        try {
          // Calculate bounding box of all nodes to get the bounds
          const padding = 50;
          let minX = Infinity,
            minY = Infinity,
            maxX = -Infinity,
            maxY = -Infinity;

          for (const node of nodes) {
            const width = (node.measured?.width || node.width || 150) as number;
            const height = (node.measured?.height ||
              node.height ||
              80) as number;
            minX = Math.min(minX, node.position.x);
            minY = Math.min(minY, node.position.y);
            maxX = Math.max(maxX, node.position.x + width);
            maxY = Math.max(maxY, node.position.y + height);
          }

          minX -= padding;
          minY -= padding;
          maxX += padding;
          maxY += padding;

          const imageWidth = 800;
          const imageHeight = 600;

          // Use ReactFlow's getViewportForBounds to calculate proper viewport
          const { getViewportForBounds } = await import('@xyflow/react');
          const targetViewport = getViewportForBounds(
            { x: minX, y: minY, width: maxX - minX, height: maxY - minY },
            imageWidth,
            imageHeight,
            0.5,
            2,
            0
          );

          // Save current viewport to restore later
          const currentViewport = reactFlowInstance.getViewport();

          // Set viewport to fit all nodes
          await reactFlowInstance.setViewport(targetViewport, { duration: 0 });

          // Wait for viewport to update and render
          await new Promise(resolve => globalThis.setTimeout(resolve, 50));

          // Get the ReactFlow container element for capture
          // We need to capture the entire .react-flow element, not just viewport,
          // because edges are rendered in a separate SVG layer
          const reactFlowElement = reactFlowWrapperRef.current.querySelector(
            '.react-flow'
          ) as HTMLElement;

          if (!reactFlowElement) {
            console.warn('[Thumbnail] ReactFlow element not found');
          } else {
            // WORKAROUND: html-to-image doesn't capture CSS styles on SVG paths properly
            // We need to add inline styles to edge paths before capture
            const edgePaths = reactFlowElement.querySelectorAll(
              '.react-flow__edge path'
            );
            const originalStyles: { element: SVGPathElement; style: string }[] =
              [];

            // Hide edge reconnect handle circles during thumbnail capture
            const edgeCircles = reactFlowElement.querySelectorAll(
              '.react-flow__edge circle'
            );
            const originalCircleDisplays: {
              element: HTMLElement;
              display: string;
            }[] = [];
            edgeCircles.forEach(circle => {
              const svgCircle = circle as HTMLElement;
              originalCircleDisplays.push({
                element: svgCircle,
                display: svgCircle.style.display,
              });
              svgCircle.style.display = 'none';
            });

            edgePaths.forEach(path => {
              const svgPath = path as SVGPathElement;
              // Save original inline style
              originalStyles.push({
                element: svgPath,
                style: svgPath.getAttribute('style') || '',
              });

              // Get computed styles and apply inline
              const computed = window.getComputedStyle(svgPath);
              const stroke = computed.stroke || '#b1b1b7';
              const strokeWidth = computed.strokeWidth || '1';
              const fill = computed.fill || 'none';

              svgPath.style.stroke = stroke;
              svgPath.style.strokeWidth = strokeWidth;
              svgPath.style.fill = fill;
            });

            // WORKAROUND: Also fix edge labels - they use foreignObject with HTML
            // that has CSS backgrounds not being captured
            const originalLabelStyles: {
              element: HTMLElement;
              style: string;
            }[] = [];

            // Fix SVG rect elements used as edge label backgrounds
            // These are the black boxes - they need fill color set inline
            const originalRectStyles: {
              element: SVGRectElement;
              style: string;
            }[] = [];
            const edgeLabelBgRects = reactFlowElement.querySelectorAll(
              '.react-flow__edge-textbg'
            );
            edgeLabelBgRects.forEach(rect => {
              const svgRect = rect as SVGRectElement;
              originalRectStyles.push({
                element: svgRect,
                style: svgRect.getAttribute('style') || '',
              });

              // Get computed fill color and apply inline
              const computed = window.getComputedStyle(svgRect);
              const fill = computed.fill || 'white';
              svgRect.style.fill = fill === 'none' ? 'white' : fill;
            });

            // Fix edge label containers (the foreignObject content)
            const edgeLabels = reactFlowElement.querySelectorAll(
              '.react-flow__edge-textwrapper, .react-flow__edge-text, .react-flow__edgelabel-renderer > div'
            );
            edgeLabels.forEach(label => {
              const htmlLabel = label as HTMLElement;
              originalLabelStyles.push({
                element: htmlLabel,
                style: htmlLabel.getAttribute('style') || '',
              });

              const computed = window.getComputedStyle(htmlLabel);
              htmlLabel.style.backgroundColor =
                computed.backgroundColor || 'transparent';
              htmlLabel.style.color = computed.color || '#000';
            });

            // Also fix any nested divs/spans in edge labels
            const labelInnerElements = reactFlowElement.querySelectorAll(
              '.react-flow__edgelabel-renderer div, .react-flow__edgelabel-renderer span'
            );
            labelInnerElements.forEach(el => {
              const htmlEl = el as HTMLElement;
              originalLabelStyles.push({
                element: htmlEl,
                style: htmlEl.getAttribute('style') || '',
              });

              const computed = window.getComputedStyle(htmlEl);
              // Force white/transparent background instead of potentially problematic values
              const bgColor = computed.backgroundColor;
              if (bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent') {
                htmlEl.style.backgroundColor = 'transparent';
              } else {
                htmlEl.style.backgroundColor = bgColor || 'white';
              }
              htmlEl.style.color = computed.color || '#000';
            });

            // Generate PNG from the ReactFlow element
            // PNG rasterizes the image, avoiding foreignObject issues with SVG
            const pngDataUrl = await toPng(reactFlowElement, {
              backgroundColor: '#ffffff',
              width: imageWidth,
              height: imageHeight,
              pixelRatio: 2, // Higher quality
              style: {
                width: `${imageWidth}px`,
                height: `${imageHeight}px`,
              },
              // Skip external resources that might fail
              skipFonts: true,
              // Filter out problematic elements
              filter: (node: Element) => {
                // Skip controls, minimap, attribution etc.
                const className = node.classList?.value || '';
                if (
                  className.includes('react-flow__controls') ||
                  className.includes('react-flow__minimap') ||
                  className.includes('react-flow__attribution') ||
                  className.includes('react-flow__panel')
                ) {
                  return false;
                }

                return true;
              },
            });

            // Restore original styles on edge paths
            originalStyles.forEach(({ element, style }) => {
              element.setAttribute('style', style);
            });

            // Restore visibility of edge reconnect handle circles
            originalCircleDisplays.forEach(({ element, display }) => {
              element.style.display = display;
            });

            // Restore original styles on edge label background rects
            originalRectStyles.forEach(({ element, style }) => {
              element.setAttribute('style', style);
            });

            // Restore original styles on edge labels
            originalLabelStyles.forEach(({ element, style }) => {
              element.setAttribute('style', style);
            });

            // Extract base64 data from PNG data URL
            // PNG data URL format: data:image/png;base64,<base64data>
            thumbnail = pngDataUrl.replace('data:image/png;base64,', '');
          }

          // Restore original viewport
          await reactFlowInstance.setViewport(currentViewport, { duration: 0 });
        } catch (error) {
          console.error('[Thumbnail] Failed to generate:', error);
          thumbnail = '';
        }
      }

      if (onSave) {
        await onSave(diagramData, thumbnail);
      }
    } catch (error) {
      console.error('Failed to save diagram:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle relation form submission
  const handleRelationSubmit = async (data: CreateRelationDto) => {
    if (!fromComponent || !toComponent) return;

    try {
      // Check if we're editing or creating
      if (relationFormMode === 'edit' && editingRelation) {
        // Update existing relation in backend
        await relationsService.update(editingRelation.id, {
          type: data.type,
          description: data.description,
          criticality: data.criticality || undefined,
          protocol: data.protocol || undefined,
          port: data.port || undefined,
          isEncrypted: data.isEncrypted,
        });

        // Update the edge label in the diagram
        setEdges((eds: Edge[]) =>
          eds.map(edge => {
            if (edge.data?.relationId === editingRelation.id) {
              return {
                ...edge,
                label: t(`relation.types.${data.type}`, {
                  defaultValue: data.type,
                }),
                data: { ...edge.data, relationType: data.type },
              };
            }
            return edge;
          })
        );

        setSnackbar({
          open: true,
          message: t('relation.messages.updated'),
          severity: 'success',
        });
      } else if (pendingConnection) {
        // Create new relation in backend
        const relation = await relationsService.create({
          fromEntityId: fromComponent.id,
          toEntityId: toComponent.id,
          type: data.type,
          description: data.description,
          criticality: data.criticality || undefined,
          protocol: data.protocol || undefined,
          port: data.port || undefined,
          isEncrypted: data.isEncrypted,
        });

        // Get source and target nodes to calculate optimal handles
        const sourceNode = nodes.find(n => n.id === pendingConnection.source);
        const targetNode = nodes.find(n => n.id === pendingConnection.target);

        // Calculate optimal handles if nodes are found
        let optimalHandles = {
          sourceHandle: 'bottom-source',
          targetHandle: 'top-target',
        };
        if (sourceNode && targetNode) {
          optimalHandles = getOptimalHandles(sourceNode, targetNode);
        }

        // Add edge to diagram with actual relation ID and optimal handles
        setEdges((eds: Edge[]) =>
          addEdge(
            {
              ...pendingConnection,
              sourceHandle:
                pendingConnection.sourceHandle || optimalHandles.sourceHandle,
              targetHandle:
                pendingConnection.targetHandle || optimalHandles.targetHandle,
              label: t(`relation.types.${data.type}`, {
                defaultValue: data.type,
              }),
              type: 'smoothstep',
              data: { relationId: relation.id, relationType: data.type },
            },
            eds
          )
        );
      }

      // Close dialog and reset state
      setRelationFormOpen(false);
      setPendingConnection(null);
      setFromComponent(null);
      setToComponent(null);
      setEditingRelation(null);
      setRelationFormMode('create');
    } catch (error) {
      console.error('Failed to save relation:', error);
      throw error;
    }
  };

  // Zoom controls
  const handleZoomIn = () => reactFlowInstance.zoomIn();
  const handleZoomOut = () => reactFlowInstance.zoomOut();
  const handleFitView = () => reactFlowInstance.fitView();

  // Toggle snap to grid functionality
  const handleToggleSnap = () => {
    setSnapToGrid(prev => !prev);
  };

  // Handle selecting a component from the sidebar
  const handleSelectComponent = async (component: Component) => {
    const nodeId = `node-${component.id}`;

    // Check if node already exists in diagram
    const existingNode = nodes.find(n => n.id === nodeId);
    if (existingNode) {
      // Node already exists, just focus on it
      reactFlowInstance.fitView({ nodes: [existingNode], duration: 300 });
      return;
    }

    // Fetch risks for this component
    let riskInfo: { highestScore: number; count: number } | undefined;
    try {
      const risks = await riskApi.getByComponent(component.id);
      if (risks.length > 0) {
        const highestScore = Math.max(
          ...risks.map(r => r.residualRisk ?? r.inherentRisk ?? 0)
        );
        riskInfo = { highestScore, count: risks.length };
      }
    } catch (error) {
      console.error('Failed to fetch risks for component:', error);
    }

    // Calculate proper grid-aligned position
    const gridSize = snapToGrid ? 15 : 1;
    const baseX = 100;
    const baseY = 100;
    const randomOffsetX =
      Math.floor((Math.random() * 200) / gridSize) * gridSize;
    const randomOffsetY =
      Math.floor((Math.random() * 200) / gridSize) * gridSize;
    const alignedX = Math.round((baseX + randomOffsetX) / gridSize) * gridSize;
    const alignedY = Math.round((baseY + randomOffsetY) / gridSize) * gridSize;

    // Use 'zone' node type for zone/security_zone components (renders as container)
    const isZoneType =
      component.type === 'zone' || component.type === 'security_zone';

    const defaultWidth = isZoneType
      ? Math.round(500 / gridSize) * gridSize
      : 150;
    const defaultHeight = isZoneType
      ? Math.round(400 / gridSize) * gridSize
      : 80;

    const newNode: Node = {
      id: nodeId,
      type: isZoneType ? 'zone' : 'custom',
      position: { x: alignedX, y: alignedY },
      data: {
        componentId: component.id,
        label: component.name,
        type: component.type,
        // CIA scores
        confidentialityScore: component.confidentialityScore,
        integrityScore: component.integrityScore,
        availabilityScore: component.availabilityScore,
        // Risk info
        highestRiskScore: riskInfo?.highestScore,
        riskCount: riskInfo?.count,
      },
      // Set both direct dimensions and style dimensions
      width: defaultWidth,
      height: defaultHeight,
      ...(isZoneType && {
        style: {
          width: defaultWidth,
          height: defaultHeight,
        },
      }),
    };
    setNodes((nds: Node[]) => [...nds, newNode]);
  };

  // Handle creating a new component (opens form wizard)
  const handleCreateComponent = () => {
    setComponentFormOpen(true);
  };

  // Handle submitting a new component from the wizard
  const handleComponentFormSubmit = async (data: CreateComponentDto) => {
    const newComponent = await componentsService.create(data);

    // Invalidate components query to refresh the sidebar list
    queryClient.invalidateQueries({ queryKey: ['components'], exact: false });

    const nodeId = `node-${newComponent.id}`;

    // Check if node already exists (shouldn't happen, but safety check)
    const existingNode = nodes.find(n => n.id === nodeId);
    if (existingNode) {
      return;
    }

    // Calculate proper grid-aligned position
    const gridSize = snapToGrid ? 15 : 1;
    const baseX = 100;
    const baseY = 100;
    const randomOffsetX =
      Math.floor((Math.random() * 200) / gridSize) * gridSize;
    const randomOffsetY =
      Math.floor((Math.random() * 200) / gridSize) * gridSize;
    const alignedX = Math.round((baseX + randomOffsetX) / gridSize) * gridSize;
    const alignedY = Math.round((baseY + randomOffsetY) / gridSize) * gridSize;

    // Use 'zone' node type for zone/security_zone components (renders as container)
    const isZoneType =
      newComponent.type === 'zone' || newComponent.type === 'security_zone';

    const defaultWidth = isZoneType
      ? Math.round(500 / gridSize) * gridSize
      : 150;
    const defaultHeight = isZoneType
      ? Math.round(400 / gridSize) * gridSize
      : 80;

    // Add the new component to the diagram
    const newNode: Node = {
      id: nodeId,
      type: isZoneType ? 'zone' : 'custom',
      position: { x: alignedX, y: alignedY },
      data: {
        componentId: newComponent.id,
        label: newComponent.name,
        type: newComponent.type,
        // CIA scores from newly created component
        confidentialityScore: newComponent.confidentialityScore,
        integrityScore: newComponent.integrityScore,
        availabilityScore: newComponent.availabilityScore,
        // No risks yet for a new component
        highestRiskScore: undefined,
        riskCount: undefined,
      },
      // Set both direct dimensions and style dimensions
      width: defaultWidth,
      height: defaultHeight,
      ...(isZoneType && {
        style: {
          width: defaultWidth,
          height: defaultHeight,
        },
      }),
    };
    setNodes((nds: Node[]) => [...nds, newNode]);
  };

  // Handle node selection to update Security Assistant context
  const handleNodeClick = useCallback(
    async (_event: React.MouseEvent, node: Node) => {
      const componentId = node.data?.componentId as string;
      if (componentId && !componentId.startsWith('temp-')) {
        try {
          const component = await componentsService.getById(componentId);
          setSelectedComponent(component);

          // Update Security Assistant context with selected component (if available)
          setComponentContext?.({
            spaceId,
            componentId: component.id,
            componentName: component.name,
            componentType: component.type,
            confidentialityScore: component.confidentialityScore,
            integrityScore: component.integrityScore,
            availabilityScore: component.availabilityScore,
            dataCategories: component.dataCategories || [],
          });
        } catch (error) {
          console.error('Failed to load component:', error);
          setSelectedComponent(null);
          clearContext?.();
        }
      } else {
        setSelectedComponent(null);
        clearContext?.();
      }
    },
    [spaceId, setComponentContext, clearContext]
  );

  // Handle right-click on node to show context menu
  const handleNodeContextMenu = useCallback(
    (event: React.MouseEvent, node: Node) => {
      event.preventDefault();
      setContextMenu({
        mouseX: event.clientX,
        mouseY: event.clientY,
        nodeId: node.id,
      });
    },
    []
  );

  // Close context menu
  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  // Remove node from diagram (keeps component in database)
  const handleRemoveFromDiagram = useCallback(() => {
    if (contextMenu) {
      // Remove the node
      setNodes(nds => nds.filter(n => n.id !== contextMenu.nodeId));
      // Remove any edges connected to this node
      setEdges(eds =>
        eds.filter(
          e =>
            e.source !== contextMenu.nodeId && e.target !== contextMenu.nodeId
        )
      );
      handleCloseContextMenu();
    }
  }, [contextMenu, setNodes, setEdges]);

  // Remove node from its parent zone (keeps node in diagram with absolute position)
  const handleRemoveFromZone = useCallback(() => {
    if (!contextMenu) return;

    const node = nodes.find(n => n.id === contextMenu.nodeId);
    if (!node || !node.parentId) return;

    // Find the parent zone to get its position
    const parentZone = nodes.find(n => n.id === node.parentId);

    // Convert relative position to absolute position
    const absoluteX = node.position.x + (parentZone?.position.x || 0);
    const absoluteY = node.position.y + (parentZone?.position.y || 0);

    // Update the node: remove parentId and extent, set absolute position
    setNodes(nds =>
      nds.map(n =>
        n.id === contextMenu.nodeId
          ? {
              ...n,
              position: { x: absoluteX, y: absoluteY },
              parentId: undefined,
              extent: undefined,
            }
          : n
      )
    );

    handleCloseContextMenu();
  }, [contextMenu, nodes, setNodes]);

  // Navigate to component details page
  const handleViewComponentDetails = useCallback(() => {
    if (!contextMenu) return;

    const node = nodes.find(n => n.id === contextMenu.nodeId);
    if (!node) return;

    const componentId = node.data?.componentId as string;
    if (!componentId || componentId.startsWith('temp-')) {
      setSnackbar({
        open: true,
        message: t('component.messages.notSaved'),
        severity: 'warning',
      });
      handleCloseContextMenu();
      return;
    }

    // Navigate to component details page
    navigate(`/components/${componentId}`);
    handleCloseContextMenu();
  }, [contextMenu, nodes, navigate, t]);

  // Handle right-click on edge to show context menu
  const handleEdgeContextMenu: EdgeMouseHandler = useCallback((event, edge) => {
    event.preventDefault();
    const relationId = edge.data?.relationId as string;
    if (relationId) {
      setEdgeContextMenu({
        mouseX: event.clientX,
        mouseY: event.clientY,
        edgeId: edge.id,
        relationId: relationId,
      });
    }
  }, []);

  // Close edge context menu
  const handleCloseEdgeContextMenu = () => {
    setEdgeContextMenu(null);
  };

  // Edit relation from edge context menu
  const handleEditRelation = useCallback(async () => {
    if (!edgeContextMenu) return;

    try {
      // Fetch the relation details
      const relation = await relationsService.getById(
        edgeContextMenu.relationId
      );

      // Find the edge to get source/target node info
      const edge = edges.find(e => e.id === edgeContextMenu.edgeId);
      if (!edge) return;

      // Get the component IDs from node data
      const sourceNode = nodes.find(n => n.id === edge.source);
      const targetNode = nodes.find(n => n.id === edge.target);
      if (!sourceNode || !targetNode) return;

      const sourceComponentId = sourceNode.data?.componentId as string;
      const targetComponentId = targetNode.data?.componentId as string;

      // Set up for edit mode
      setEditingRelation({
        id: relation.id,
        type: relation.type,
        fromEntityId: relation.fromEntityId,
        toEntityId: relation.toEntityId,
        description: relation.description,
        criticality: relation.criticality,
      });

      // Get component details for the relation form
      const [sourceComp, targetComp] = await Promise.all([
        componentsService.getById(sourceComponentId),
        componentsService.getById(targetComponentId),
      ]);

      setFromComponent({
        id: sourceComp.id,
        name: sourceComp.name,
        type: sourceComp.type as ComponentType,
      });
      setToComponent({
        id: targetComp.id,
        name: targetComp.name,
        type: targetComp.type as ComponentType,
      });

      setRelationFormMode('edit');
      setRelationFormOpen(true);
      handleCloseEdgeContextMenu();
    } catch (error) {
      console.error('Failed to load relation for editing:', error);
      setSnackbar({
        open: true,
        message: t('relation.messages.loadError'),
        severity: 'error',
      });
    }
  }, [edgeContextMenu, edges, nodes, t]);

  // Confirm delete relation from edge context menu
  const handleConfirmDeleteRelation = useCallback(() => {
    if (!edgeContextMenu) return;

    const edge = edges.find(e => e.id === edgeContextMenu.edgeId);
    const relationLabel = edge?.label || edge?.data?.type || 'Relation';

    setDeleteConfirmDialog({
      type: 'edge',
      id: edgeContextMenu.relationId,
      name: String(relationLabel),
    });
    handleCloseEdgeContextMenu();
  }, [edgeContextMenu, edges]);

  // Delete relation and remove edge
  const handleDeleteRelation = useCallback(async () => {
    if (!deleteConfirmDialog || deleteConfirmDialog.type !== 'edge') return;

    try {
      await relationsService.deleteRelation(deleteConfirmDialog.id);

      // Remove the edge from the diagram
      setEdges(eds =>
        eds.filter(e => e.data?.relationId !== deleteConfirmDialog.id)
      );

      setSnackbar({
        open: true,
        message: t('relation.messages.deleted'),
        severity: 'success',
      });
    } catch (error) {
      console.error('Failed to delete relation:', error);
      setSnackbar({
        open: true,
        message: t('relation.messages.deleteError'),
        severity: 'error',
      });
    } finally {
      setDeleteConfirmDialog(null);
    }
  }, [deleteConfirmDialog, setEdges, t]);

  // Confirm delete component from node context menu
  const handleConfirmDeleteComponent = useCallback(async () => {
    if (!contextMenu) return;

    const node = nodes.find(n => n.id === contextMenu.nodeId);
    if (!node) return;

    const componentId = node.data?.componentId as string;
    if (!componentId || componentId.startsWith('temp-')) {
      // Just remove from diagram if temp node
      handleRemoveFromDiagram();
      return;
    }

    try {
      // Fetch component to check if global and get relation count
      setDeleteConfirmDialog({
        type: 'node',
        id: componentId,
        name: (node.data?.label as string) || 'Component',
        loading: true,
      });

      const component = await componentsService.getById(componentId);
      const relations = await relationsService.getByEntityId(componentId);

      setDeleteConfirmDialog({
        type: 'node',
        id: componentId,
        name: component.name,
        isGlobal: component.isGlobal,
        relationCount: relations.length,
        loading: false,
      });
      handleCloseContextMenu();
    } catch (error) {
      console.error('Failed to fetch component details:', error);
      setDeleteConfirmDialog(null);
      setSnackbar({
        open: true,
        message: t('component.messages.fetchError'),
        severity: 'error',
      });
    }
  }, [contextMenu, nodes, handleRemoveFromDiagram, t]);

  // Delete component from database
  const handleDeleteComponent = useCallback(async () => {
    if (!deleteConfirmDialog || deleteConfirmDialog.type !== 'node') return;

    try {
      await componentsService.deleteComponent(deleteConfirmDialog.id);

      // Remove all nodes with this component ID (might be in multiple diagrams)
      const nodeIdToRemove = nodes.find(
        n => n.data?.componentId === deleteConfirmDialog.id
      )?.id;

      if (nodeIdToRemove) {
        setNodes(nds => nds.filter(n => n.id !== nodeIdToRemove));
        // Remove any edges connected to this node
        setEdges(eds =>
          eds.filter(
            e => e.source !== nodeIdToRemove && e.target !== nodeIdToRemove
          )
        );
      }

      setSnackbar({
        open: true,
        message: t('component.messages.deleted'),
        severity: 'success',
      });

      // Refresh component list in selector
      queryClient.invalidateQueries({ queryKey: ['components', spaceId] });
    } catch (error) {
      console.error('Failed to delete component:', error);
      setSnackbar({
        open: true,
        message: t('component.messages.deleteError'),
        severity: 'error',
      });
    } finally {
      setDeleteConfirmDialog(null);
    }
  }, [deleteConfirmDialog, nodes, setNodes, setEdges, t, queryClient, spaceId]);

  // Handle edge reconnection
  const onReconnect: OnReconnect = useCallback(
    async (oldEdge, newConnection) => {
      const relationId = oldEdge.data?.relationId as string;
      if (!relationId) return;

      // Get the new source and target component IDs
      const sourceNode = nodes.find(n => n.id === newConnection.source);
      const targetNode = nodes.find(n => n.id === newConnection.target);
      if (!sourceNode || !targetNode) return;

      const newFromEntityId = sourceNode.data?.componentId as string;
      const newToEntityId = targetNode.data?.componentId as string;

      try {
        // Update the relation in the backend
        await relationsService.update(relationId, {
          fromEntityId: newFromEntityId,
          toEntityId: newToEntityId,
        });

        // Update the edge in the diagram
        setEdges(eds => reconnectEdge(oldEdge, newConnection, eds));

        setSnackbar({
          open: true,
          message: t('relation.messages.updated'),
          severity: 'success',
        });
      } catch (error) {
        console.error('Failed to update relation:', error);
        setSnackbar({
          open: true,
          message: t('relation.messages.updateError'),
          severity: 'error',
        });
      }
    },
    [nodes, setEdges, t]
  );

  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setDeleteConfirmDialog(null);
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: 'calc(100vh - 64px)',
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      {/* Component Library Sidebar - hidden in read-only mode */}
      {!isReadOnly && (
        <Drawer
          variant="persistent"
          anchor="left"
          open={isSidebarOpen}
          sx={{
            width: 280,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: 280,
              boxSizing: 'border-box',
              mt: 8,
              height: 'calc(100vh - 64px)',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {t('diagram.components')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Button
              fullWidth
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleCreateComponent}
              sx={{ mb: 2 }}
            >
              {t('component.create')}
            </Button>
          </Box>
          <ComponentSelector
            spaceId={spaceId}
            onSelectComponent={handleSelectComponent}
            onCreateComponent={handleCreateComponent}
          />
        </Drawer>
      )}

      {/* Main Diagram Canvas */}
      <Box
        ref={reactFlowWrapperRef}
        sx={{
          flexGrow: 1,
          height: '100%',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <ReactFlow
          nodes={nodes.map(node => ({
            ...node,
            data: { ...node.data, isConnecting },
          }))}
          edges={edges.map(edge => ({
            ...edge,
            markerEnd: {
              type: MarkerType.ArrowClosed,
              width: 20,
              height: 20,
              color: edge.style?.stroke || '#b1b1b7',
            },
          }))}
          onNodesChange={isReadOnly ? undefined : onNodesChange}
          onEdgesChange={isReadOnly ? undefined : onEdgesChange}
          onConnect={isReadOnly ? undefined : onConnect}
          onConnectStart={isReadOnly ? undefined : () => setIsConnecting(true)}
          onConnectEnd={isReadOnly ? undefined : () => setIsConnecting(false)}
          onNodeDragStop={isReadOnly ? undefined : onNodeDragStop}
          onNodeClick={isReadOnly ? undefined : handleNodeClick}
          onNodeContextMenu={isReadOnly ? undefined : handleNodeContextMenu}
          onEdgeContextMenu={isReadOnly ? undefined : handleEdgeContextMenu}
          onReconnect={isReadOnly ? undefined : onReconnect}
          onPaneClick={
            isReadOnly
              ? undefined
              : () => {
                  setSelectedComponent(null);
                  clearContext?.();
                }
          }
          nodeTypes={nodeTypes}
          nodesDraggable={!isReadOnly}
          nodesConnectable={!isReadOnly}
          elementsSelectable={!isReadOnly}
          edgesReconnectable={!isReadOnly}
          snapToGrid={isReadOnly ? false : snapToGrid}
          snapGrid={[15, 15]}
          fitView
          proOptions={{ hideAttribution: true }}
        >
          <Background variant={BackgroundVariant.Dots} gap={15} size={1} />

          {/* Custom Toolbar - hidden in read-only mode */}
          {!isReadOnly && (
            <Panel position="top-right">
              <Paper sx={{ p: 1, display: 'flex', gap: 1 }}>
                <Tooltip title={t('diagram.zoomIn')}>
                  <IconButton size="small" onClick={handleZoomIn}>
                    <ZoomInIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('diagram.zoomOut')}>
                  <IconButton size="small" onClick={handleZoomOut}>
                    <ZoomOutIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('diagram.fitView')}>
                  <IconButton size="small" onClick={handleFitView}>
                    <FitScreenIcon />
                  </IconButton>
                </Tooltip>
                <Divider orientation="vertical" flexItem />
                <Tooltip
                  title={
                    snapToGrid
                      ? t('diagram.disableSnap')
                      : t('diagram.enableSnap')
                  }
                >
                  <IconButton
                    size="small"
                    onClick={handleToggleSnap}
                    color={snapToGrid ? 'primary' : 'default'}
                  >
                    <GridOnIcon />
                  </IconButton>
                </Tooltip>
                <Divider orientation="vertical" flexItem />
                <Tooltip title={t('common.save')}>
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={handleSave}
                      disabled={isSaving}
                    >
                      <SaveIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Paper>
            </Panel>
          )}
        </ReactFlow>
      </Box>

      {/* Relation Creation/Edit Dialog */}
      {fromComponent && toComponent && (
        <RelationForm
          isOpen={relationFormOpen}
          onClose={() => {
            setRelationFormOpen(false);
            setPendingConnection(null);
            setFromComponent(null);
            setToComponent(null);
            setEditingRelation(null);
            setRelationFormMode('create');
          }}
          onSubmit={handleRelationSubmit}
          fromComponent={fromComponent}
          toComponent={toComponent}
          mode={relationFormMode}
          initialData={
            editingRelation
              ? {
                  type: editingRelation.type as RelationType,
                  description: editingRelation.description,
                  criticality: editingRelation.criticality as
                    | CriticalityLevel
                    | undefined,
                }
              : undefined
          }
          onSwapDirection={() => {
            // Swap source and target
            setFromComponent(toComponent);
            setToComponent(fromComponent);
          }}
        />
      )}

      {/* Component Creation Dialog */}
      <ComponentFormWizard
        open={componentFormOpen}
        onClose={() => setComponentFormOpen(false)}
        onSubmit={handleComponentFormSubmit}
        currentSpaceId={spaceId}
      />

      {/* Node Context Menu */}
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleViewComponentDetails}>
          <ListItemIcon>
            <OpenInNewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('component.viewDetails')}</ListItemText>
        </MenuItem>
        <Divider />
        {/* Show "Remove from Zone" only when node has a parent zone */}
        {contextMenu &&
          nodes.find(n => n.id === contextMenu.nodeId)?.parentId && (
            <>
              <MenuItem onClick={handleRemoveFromZone}>
                <ListItemIcon>
                  <ExitToAppIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{t('diagram.removeFromZone')}</ListItemText>
              </MenuItem>
              <Divider />
            </>
          )}
        <MenuItem onClick={handleRemoveFromDiagram}>
          <ListItemIcon>
            <RemoveFromDiagramIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('diagram.removeFromDiagram')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleConfirmDeleteComponent}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('component.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Edge Context Menu */}
      <Menu
        open={edgeContextMenu !== null}
        onClose={handleCloseEdgeContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          edgeContextMenu !== null
            ? { top: edgeContextMenu.mouseY, left: edgeContextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleEditRelation}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('relation.edit')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={handleConfirmDeleteRelation}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('relation.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog !== null}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {deleteConfirmDialog?.type === 'node'
            ? t('component.deleteTitle')
            : t('relation.deleteTitle')}
        </DialogTitle>
        <DialogContent>
          {deleteConfirmDialog?.loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography gutterBottom>
                {deleteConfirmDialog?.type === 'node'
                  ? t('component.deleteMessage', {
                      name: deleteConfirmDialog?.name,
                    })
                  : t('relation.deleteMessage', {
                      name: deleteConfirmDialog?.name,
                    })}
              </Typography>

              {/* Global component warning */}
              {deleteConfirmDialog?.type === 'node' &&
                deleteConfirmDialog?.isGlobal && (
                  <Alert
                    severity="error"
                    icon={<WarningIcon />}
                    sx={{ mt: 2, mb: 2 }}
                  >
                    <Typography variant="subtitle2" fontWeight="bold">
                      {t('component.globalWarningTitle')}
                    </Typography>
                    <Typography variant="body2">
                      {t('component.globalWarningMessage')}
                    </Typography>
                  </Alert>
                )}

              {/* Relation count warning */}
              {deleteConfirmDialog?.type === 'node' &&
                (deleteConfirmDialog?.relationCount ?? 0) > 0 && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {t('component.relationWarning', {
                      count: deleteConfirmDialog?.relationCount,
                    })}
                  </Alert>
                )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteDialog}
            disabled={deleteConfirmDialog?.loading}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={
              deleteConfirmDialog?.type === 'node'
                ? handleDeleteComponent
                : handleDeleteRelation
            }
            color="error"
            variant="contained"
            disabled={deleteConfirmDialog?.loading}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Security Assistant is now provided via AppLayout - context is set on node selection */}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Wrapper with ReactFlowProvider
export const DiagramBuilder: React.FC<DiagramBuilderProps> = props => {
  return (
    <ReactFlowProvider>
      <DiagramBuilderComponent {...props} />
    </ReactFlowProvider>
  );
};
